import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/patterns";

interface Rule {
  id: string;
  rule_key: string;
  rule_name: string;
  value_json: unknown;
}

const KEY = "TWO_FACTOR_REQUIRED_FOR";
const CHOICES = [
  { value: "NONE", label: "Nobody", note: "Two-step verification is optional. People can turn it on for themselves under Settings, Preferences, Security." },
  { value: "ADMINS", label: "Super Admins and Sales Heads", note: "The people who can see and change the most. They must use an authenticator app." },
  { value: "ALL", label: "Everyone", note: "Every user must use an authenticator app." },
] as const;

/** Settings → Sign-in security (Super Admin): who must use two-step verification */
export function SignInSecurityPage() {
  const { success, error } = useToast();
  const [rule, setRule] = useState<Rule | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRule(unwrapArray<Rule>(await api.get("/api/SettingRule/List")).find((r) => r.rule_key === KEY) ?? null);
    } catch {
      setRule(null);
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const value = String(rule?.value_json ?? "NONE").toUpperCase();

  const choose = async (next: string) => {
    if (!rule || busy || next === value) return;
    setBusy(true);
    try {
      await api.put(`/api/SettingRule/Update/${rule.id}`, { rule_key: rule.rule_key, rule_name: rule.rule_name, value_json: next });
      success("Saved", next === "NONE" ? "Two-step verification is optional again" : "People who have not set it up yet will be asked to the next time they open the CRM");
      await load();
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title="Sign-in security" />
      <section aria-labelledby="require-heading" className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div>
          <h2 id="require-heading" className="text-sm font-semibold text-foreground">Require two-step verification for</h2>
          <p className="text-xs text-muted-foreground">
            After the password, signing in also asks for a 6-digit code from an authenticator app (Google Authenticator, Microsoft Authenticator, Authy). It needs no Google or Microsoft account.
          </p>
        </div>
        {!loaded && (
          <div role="status" className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Loading…
          </div>
        )}
        {loaded && !rule && <p role="alert" className="text-sm text-danger">This setting is not in the database yet. Run the latest update and reload.</p>}
        {rule && (
          <fieldset disabled={busy} className="space-y-2">
            <legend className="sr-only">Who must use two-step verification</legend>
            {CHOICES.map((c) => (
              <label key={c.value} className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${value === c.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                <input type="radio" name="two-factor-required" value={c.value} checked={value === c.value} onChange={() => choose(c.value)} className="mt-1 size-4 cursor-pointer accent-primary" />
                <span>
                  <span className="block text-sm font-medium text-foreground">{c.label}</span>
                  <span className="block text-xs text-muted-foreground">{c.note}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          Someone who has lost their phone can use a backup code to sign in. If they have lost those too, reset their two-step verification from the Users page.
        </p>
      </section>
    </div>
  );
}
